import React, { useRef, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { FiX, FiUsers, FiStar, FiEdit3, FiTrash2, FiGift, FiGitBranch, FiUserCheck, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import api from '../services/api';
import CongratulationSendMobile from './CongratulationSendMobile';
import CompanyEmployeesListModal from './Modals/CompanyEmployeesListModal';
import CompanyStructureModal from './Modals/CompanyStructureModal';
import OnboardingModal from './Modals/OnboardingModal';
import { ENABLE_MODAL_SWIPE_BACK } from '../utils/mobileModalSwipe';

const employeesMenuSidebarLikeStyleMobile = {
  width: '100%',
  minHeight: 48,
  touchAction: 'manipulation',
  borderRadius: 10,
  border: 'none',
  padding: '12px 16px',
  fontSize: 'var(--sidebar-text-size, 1.08em)',
  fontFamily: 'var(--sidebar-font-family, inherit)',
  fontWeight: 600,
  color: 'var(--sidebar-text-color, #fff)',
  background: 'var(--sidebar-button-background, #232931)',
  boxShadow: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 10,
  cursor: 'pointer',
  transition: 'all .18s',
};

export default function CongratulationsMobile({ open, onClose, onOpenMobileSidebar }) {
  const modalRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersByEmployeeId, setUsersByEmployeeId] = useState({});
  const [search, setSearch] = useState('');
  const [editUser, setEditUser] = useState(null);
  const [congratsUser, setCongratsUser] = useState(null);
  const [showCompanyEmployeesList, setShowCompanyEmployeesList] = useState(false);
  const [showCompanyStructure, setShowCompanyStructure] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [actionsBarExpanded, setActionsBarExpanded] = useState(true);

  useEffect(() => {
    console.log('📱 Mobile: editUser changed:', editUser);
  }, [editUser]);

  useEffect(() => {
    console.log('📱 Mobile: congratsUser changed:', congratsUser);
  }, [congratsUser]);

  // Защита: employees всегда массив
  const safeEmployees = Array.isArray(employees) ? employees : [];

  useEffect(() => {
    if (!open) {
      setShowCompanyEmployeesList(false);
      setShowCompanyStructure(false);
      setShowOnboarding(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/api/employees'),
      api.get('/api/users').catch(() => ({ data: [] }))
    ])
      .then(([empRes, usersRes]) => {
        const empArr = Array.isArray(empRes.data) ? empRes.data : [];
        const usersArr = Array.isArray(usersRes.data) ? usersRes.data : [];
        const map = {};
        usersArr.forEach(u => {
          const key = String(u.employee_id || u.employeeId || '');
          if (key) map[key] = u;
        });
        setUsersByEmployeeId(map);
        setEmployees(empArr);
      })
      .catch(() => {
        setUsersByEmployeeId({});
        setEmployees([]);
      })
      .finally(() => setLoading(false));
  }, [open, editUser]);

  const filteredEmployees = safeEmployees.filter((e) =>
    (e.first_name + ' ' + e.last_name).toLowerCase().includes(search.trim().toLowerCase())
  );

  const handleClose = () => {
    // Просто закрываем модалку, возвращаемся к SidebarNav (который остается открытым)
    onClose();
  };

  // Функция для получения градиента по имени
  const getGradientByName = (name) => {
    const gradients = [
      'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
      'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
    ];
    const index = (name || '').length % gradients.length;
    return gradients[index];
  };

  // Приводим URL аватарок к абсолютному
  const normalizeAvatarUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const trimmed = url.trim();
    if (!trimmed) return null;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    if (trimmed.startsWith('/')) return `${api.defaults.baseURL}${trimmed}`;
    return `${api.defaults.baseURL}/${trimmed}`;
  };

  const getAge = (birth_day, birth_month, birth_year) => {
    if (!birth_day || !birth_month || !birth_year) return null;
    const today = new Date();
    let age = today.getFullYear() - birth_year;
    const m = today.getMonth() + 1 - birth_month;
    if (m < 0 || (m === 0 && today.getDate() < birth_day)) {
      age--;
    }
    return age;
  };

  const handleDelete = async (emp) => {
    if (!window.confirm(`Удалить сотрудника ${emp.first_name} ${emp.last_name}?`)) return;
    try {
      console.log('🗑️ Deleting employee with id:', emp.id);
      const response = await api.delete(`/api/employees/${emp.id}`);
      console.log('✅ Delete response:', response);
      
      // Проверяем, что ответ успешный
      if (response && response.status >= 200 && response.status < 300) {
        console.log('✅ Employee deleted successfully');
        
        // Обновляем список сотрудников
        setEmployees((prev) => prev.filter(e => e.id !== emp.id));
        
        // Обновляем карту пользователей
        const usersMap = usersByEmployeeId || {};
        const user = usersMap[String(emp.id)];
        if (user && user.id) {
          try { 
            console.log('🗑️ Deleting linked user with id:', user.id);
            await api.delete(`/api/users/${user.id}`);
            console.log('✅ Linked user deleted');
          } catch(e) {
            console.error('⚠️ Error deleting linked user:', e);
            // Не показываем ошибку пользователю, т.к. сотрудник уже удален
          }
        }
      } else {
        throw new Error('Неожиданный ответ сервера');
      }
    } catch (err) {
      console.error('❌ Error deleting employee:', err);
      console.error('❌ Error details:', {
        message: err.message,
        response: err.response,
        status: err.response?.status,
        data: err.response?.data,
        config: err.config
      });
      
      let errorMessage = 'Неизвестная ошибка';
      if (err.response) {
        // Сервер вернул ответ с ошибкой
        errorMessage = err.response.data?.error || err.response.data?.message || `Ошибка ${err.response.status}`;
      } else if (err.request) {
        // Запрос был отправлен, но ответа не получено
        errorMessage = 'Не удалось получить ответ от сервера';
      } else {
        // Ошибка при настройке запроса
        errorMessage = err.message || 'Ошибка при отправке запроса';
      }
      
      alert(`Ошибка при удалении сотрудника: ${errorMessage}`);
    }
  };

  return (
    <>
      {open && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 100002, // Выше SidebarNav (100001)
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(8px)',
          }}
          onClick={handleClose}
        >
      <div
        ref={modalRef}
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
          paddingTop: '50px',
          paddingBottom: '80px',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '50px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 16px',
            zIndex: 10001,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          }}
        >
          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
            }}
            title="Назад"
          >
            <FaArrowLeft />
          </button>
          
          <h2
            style={{
              color: '#fff',
              fontSize: '18px',
              fontWeight: 700,
              margin: 0,
              flex: 1,
              textAlign: 'center',
            }}
          >
            Сотрудники компании
          </h2>

          <button
            onClick={handleClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'background-color 0.2s',
            }}
            title="Закрыть"
          >
            <FiX />
          </button>
        </div>

        {/* Tabs и кнопки действий */}
        <div
          style={{
            position: 'sticky',
            top: '15px',
            background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
            padding: '4px 16px 8px',
            marginTop: -10,
            zIndex: 10000,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <div style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              type="button"
              aria-expanded={actionsBarExpanded}
              aria-label={actionsBarExpanded ? 'Свернуть панель действий' : 'Показать панель действий'}
              onClick={() => setActionsBarExpanded((v) => !v)}
              style={{
                ...employeesMenuSidebarLikeStyleMobile,
                width: 'auto',
                flex: '0 0 auto',
                minHeight: 44,
              }}
            >
              {actionsBarExpanded ? <FiChevronUp size={18} aria-hidden /> : <FiChevronDown size={18} aria-hidden />}
              {actionsBarExpanded ? 'Свернуть' : 'Действия'}
            </button>
            <input
              type="text"
              placeholder="🔍 Поиск..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                flex: '1 1 140px',
                minWidth: 120,
                padding: '12px 14px',
                borderRadius: '12px',
                border: '2px solid rgba(67, 233, 123, 0.3)',
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#fff',
                fontSize: '15px',
                fontWeight: 500,
                outline: 'none',
              }}
            />
          </div>

          {actionsBarExpanded && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                marginBottom: 8,
                width: '100%',
              }}
            >
              <button
                type="button"
                onClick={() => setShowCompanyEmployeesList(true)}
                style={employeesMenuSidebarLikeStyleMobile}
              >
                <FiUsers size={18} />
                Список сотрудников
              </button>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new Event('show-employee-rating'))}
                style={employeesMenuSidebarLikeStyleMobile}
              >
                <FiStar size={18} />
                Рейтинг успеваемости
              </button>
              <button
                type="button"
                onClick={() => setShowCompanyStructure(true)}
                style={employeesMenuSidebarLikeStyleMobile}
              >
                <FiGitBranch size={18} />
                Структура компании
              </button>
              <button
                type="button"
                onClick={() => setShowOnboarding(true)}
                style={employeesMenuSidebarLikeStyleMobile}
              >
                <FiUserCheck size={18} />
                Онбординг
              </button>
            </div>
          )}
        </div>

        {/* Контент */}
        <div style={{ flex: 1, padding: '16px', paddingTop: '16px', marginTop: '8px' }}>
            <>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '16px' }}>
                  Загрузка...
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', fontSize: '16px' }}>
                  Сотрудники не найдены
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {filteredEmployees.map((emp) => {
                    const user = usersByEmployeeId[String(emp.id)] || {};
                    const avatarUrl = normalizeAvatarUrl(user.avatar);
                    const initials = `${emp.first_name?.[0] || ''}${emp.last_name?.[0] || ''}`;
                    const age = getAge(emp.birth_day, emp.birth_month, emp.birth_year);
                    const birthday = emp.birth_day && emp.birth_month
                      ? `${emp.birth_day}.${emp.birth_month}${emp.birth_year ? `.${emp.birth_year}` : ''}`
                      : null;

                    return (
                      <div
                        key={emp.id}
                        style={{
                          background: 'linear-gradient(135deg, rgba(44, 62, 80, 0.9) 0%, rgba(52, 73, 94, 0.9) 100%)',
                          borderRadius: '16px',
                          padding: '16px',
                          border: '2px solid rgba(255, 255, 255, 0.1)',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div
                            style={{
                              width: '60px',
                              height: '60px',
                              borderRadius: '12px',
                              background: avatarUrl
                                ? `url(${avatarUrl}) center/cover`
                                : getGradientByName(`${emp.first_name} ${emp.last_name}`),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#fff',
                              fontWeight: 'bold',
                              fontSize: '20px',
                              flexShrink: 0,
                              border: '2px solid rgba(255, 255, 255, 0.2)',
                            }}
                          >
                            {!avatarUrl && initials}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: '#fff',
                              fontSize: '16px',
                              fontWeight: 600,
                              marginBottom: '4px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {emp.first_name} {emp.last_name}
                            </div>
                            {emp.position && (
                              <div style={{ color: '#94a3b8', fontSize: '13px', marginBottom: '4px' }}>
                                {emp.position}
                              </div>
                            )}
                            {birthday && (
                              <div style={{ color: '#43e97b', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                🎂 {birthday} {age && `(${age} лет)`}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (emp && emp.id) {
                                try {
                                  setCongratsUser(null);
                                  setEditUser(emp);
                                } catch (err) {
                                  alert('Ошибка при открытии модалки редактирования: ' + err.message);
                                }
                              }
                            }}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{
                              flex: 1,
                              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                              color: '#232931',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '10px',
                              fontSize: '14px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 8px rgba(67, 233, 123, 0.3)',
                              position: 'relative',
                              zIndex: 1000,
                              pointerEvents: 'auto',
                              touchAction: 'manipulation',
                            }}
                          >
                            <FiEdit3 size={16} />
                            Редактировать
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (emp && emp.id) {
                                try {
                                  setEditUser(null);
                                  setCongratsUser(emp);
                                } catch (err) {
                                  alert('Ошибка при открытии модалки поздравления: ' + err.message);
                                }
                              }
                            }}
                            onTouchStart={(e) => e.stopPropagation()}
                            style={{
                              flex: 1,
                              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '10px',
                              fontSize: '14px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)',
                              position: 'relative',
                              zIndex: 1000,
                              pointerEvents: 'auto',
                              touchAction: 'manipulation',
                            }}
                          >
                            <FiGift size={16} />
                            Поздравить
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(emp)}
                            style={{
                              background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '10px',
                              padding: '10px',
                              fontSize: '14px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              boxShadow: '0 2px 8px rgba(231, 76, 60, 0.3)',
                              minWidth: '50px',
                              touchAction: 'manipulation',
                            }}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
        </div>

        {/* Подсказка о свайпе */}
        {ENABLE_MODAL_SWIPE_BACK && (
        <div
          style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255, 255, 255, 0.5)',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 10002,
          }}
        >
          ← Свайпните влево для возврата
        </div>
        )}
      </div>

    </div>,
    document.body
      )}

      {editUser && ReactDOM.createPortal(
        <EditUserModal
          user={editUser}
          onClose={() => setEditUser(null)}
          onSaved={() => setEditUser(null)}
        />,
        document.body
      )}
      {congratsUser && ReactDOM.createPortal(
        (() => {
          console.log('📱 Mobile: Rendering CongratulationSendMobile with user:', congratsUser);
          return (
            <CongratulationSendMobile
              user={congratsUser}
              open={!!congratsUser}
              onClose={() => {
                console.log('📱 Mobile: Closing CongratulationSendMobile');
                setCongratsUser(null);
              }}
              onOpenMobileSidebar={() => {
                // Не открываем сайдбар, так как мы уже внутри модалки
              }}
              onSent={() => {
                console.log('📱 Mobile: Congratulation sent');
                setCongratsUser(null);
                window.dispatchEvent(new CustomEvent('news-published'));
              }}
            />
          );
        })(),
        document.body
      )}

      {open &&
        showCompanyEmployeesList &&
        ReactDOM.createPortal(
          <CompanyEmployeesListModal
            open={showCompanyEmployeesList}
            onClose={() => setShowCompanyEmployeesList(false)}
            isMobile
          />,
          document.body
        )}
      {open &&
        showCompanyStructure &&
        ReactDOM.createPortal(
          <CompanyStructureModal
            open={showCompanyStructure}
            onClose={() => setShowCompanyStructure(false)}
            isMobile
          />,
          document.body
        )}
      {open &&
        showOnboarding &&
        ReactDOM.createPortal(
          <OnboardingModal
            open={showOnboarding}
            onClose={() => setShowOnboarding(false)}
            isMobile
          />,
          document.body
        )}
    </>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [birthDate, setBirthDate] = useState(
    user.birth_year && user.birth_month && user.birth_day
      ? `${user.birth_year}-${String(user.birth_month).padStart(2, '0')}-${String(user.birth_day).padStart(2, '0')}`
      : ''
  );
  const [avatar, setAvatar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setFirstName(user.first_name || '');
    setLastName(user.last_name || '');
    setBirthDate(
      user.birth_year && user.birth_month && user.birth_day
        ? `${user.birth_year}-${String(user.birth_month).padStart(2, '0')}-${String(user.birth_day).padStart(2, '0')}`
        : ''
    );
    setAvatar(null);
  }, [user.id, user.first_name, user.last_name, user.birth_year, user.birth_month, user.birth_day]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const formData = new FormData();
    formData.append('id', user.id);
    formData.append('first_name', firstName);
    formData.append('last_name', lastName);
    if (birthDate) {
      const [year, month, day] = birthDate.split('-');
      formData.append('birth_year', year);
      formData.append('birth_month', month);
      formData.append('birth_day', day);
    }
    if (avatar) {
      formData.append('avatar', avatar);
    }
    try {
      await api.post('/api/congratulations/edit-employee', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved();
    } catch (err) {
      alert('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.9)',
        zIndex: 200000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflow: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #232931 0%, #181c22 100%)',
          borderRadius: '20px',
          width: '100%',
          maxWidth: '500px',
          padding: '24px',
          border: '2px solid rgba(67, 233, 123, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginBottom: '20px', color: '#43e97b', fontWeight: 700, fontSize: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <FiEdit3 size={20} />
          Редактировать сотрудника
        </h3>
        <form onSubmit={handleSubmit}>
          <input
            placeholder="Имя"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            style={{
              width: '100%',
              marginBottom: '12px',
              padding: '12px',
              borderRadius: '12px',
              border: '2px solid rgba(67, 233, 123, 0.3)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 500,
              outline: 'none',
            }}
          />
          <input
            placeholder="Фамилия"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            style={{
              width: '100%',
              marginBottom: '12px',
              padding: '12px',
              borderRadius: '12px',
              border: '2px solid rgba(67, 233, 123, 0.3)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 500,
              outline: 'none',
            }}
          />
          <input
            type="date"
            value={birthDate}
            onChange={(e) => setBirthDate(e.target.value)}
            style={{
              width: '100%',
              marginBottom: '12px',
              padding: '12px',
              borderRadius: '12px',
              border: '2px solid rgba(67, 233, 123, 0.3)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
              fontSize: '15px',
              fontWeight: 500,
              outline: 'none',
            }}
          />
          <label style={{ display: 'block', marginBottom: '8px', color: '#43e97b', fontWeight: 600, fontSize: '14px' }}>
            Фото сотрудника:
          </label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setAvatar(e.target.files[0])}
            style={{
              width: '100%',
              marginBottom: '16px',
              padding: '10px',
              borderRadius: '12px',
              border: '2px solid rgba(67, 233, 123, 0.3)',
              background: 'rgba(255, 255, 255, 0.05)',
              color: '#fff',
              fontSize: '14px',
            }}
          />
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                flex: 1,
                background: 'linear-gradient(135deg, #43e97b 0%, #2193b0 100%)',
                color: '#fff',
                border: 'none',
                borderRadius: '12px',
                padding: '12px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                opacity: saving ? 0.7 : 1,
              }}
            >
              {saving ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
